import React, { useMemo } from 'react';

// Reading exam picker — same layout as the writing exam-select screen so the
// student gets a consistent "Choose your paper" experience across both
// sections. Click a card → navigate to onboarding with the examId in the
// URL (gets threaded through to reading-home).

export default function ReadingExamPicker({
  theme, isMobile, isDark, navigate, toggleTheme, accountControl,
  exams = [], loading,
}) {
  // Filter to exams the admin has populated with reading content. The list
  // endpoint only ships has_reading_content (boolean); reading_parts is
  // detail-only — fall back gracefully when it's missing.
  const readingReadyExams = useMemo(() => {
    return (exams || []).filter((ex) => {
      if (typeof ex?.has_reading_content === 'boolean') return ex.has_reading_content;
      return (ex?.reading_parts || []).some((p) => p.has_content && (p.question_count || 0) > 0);
    });
  }, [exams]);

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${theme.pageBgAlt} 0%, ${theme.pageBgSoft} 100%)`,
      fontFamily: "'Outfit', sans-serif",
      color: theme.text,
    }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto', padding: isMobile ? '22px 16px 34px' : '28px 22px 50px' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              height: '42px',
              borderRadius: '999px',
              border: `1px solid ${theme.border}`,
              background: theme.surface,
              color: theme.body,
              padding: '0 16px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'inherit',
            }}
          >
            <span aria-hidden="true">←</span>
            <span>Dashboard</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: theme.body,
                borderRadius: '999px',
                width: '40px',
                height: '40px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: theme.shadow,
              }}
            >
              {isDark ? '☀' : '☾'}
            </button>
            {accountControl}
          </div>
        </div>

        {/* Hero card */}
        <section style={{
          background: theme.surface,
          borderRadius: '30px',
          border: `1px solid ${theme.border}`,
          boxShadow: theme.shadow,
          padding: isMobile ? '24px 18px' : '34px 32px',
          marginBottom: '22px',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 12px',
            borderRadius: 999,
            background: theme.accentSoftBg,
            color: theme.accent,
            fontSize: '12px',
            fontWeight: 800,
            marginBottom: '14px',
          }}>
            Select Exam
          </div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '30px' : '42px', fontWeight: 800, color: theme.text, lineHeight: 1.1 }}>
            Choose Your FET Reading Exam
          </h1>
          <p style={{ margin: '12px 0 0', fontSize: '15px', color: theme.body, lineHeight: 1.8, maxWidth: '760px' }}>
            Pick any available exam below. Each card opens that specific exam's reading flow — the same login, attempts and scoring work across all of them.
          </p>
        </section>

        {/* Grid of exam cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
        }}>
          {loading ? (
            <div style={{
              background: theme.surfaceSoft,
              borderRadius: '20px',
              padding: '24px',
              border: `1px solid ${theme.border}`,
              color: theme.body,
              lineHeight: 1.8,
            }}>
              Loading available reading exams…
            </div>
          ) : readingReadyExams.length > 0 ? readingReadyExams.map((availableExam) => (
            <div
              key={availableExam.id}
              style={{
                background: theme.surface,
                borderRadius: '24px',
                padding: '24px',
                boxShadow: theme.shadow,
                border: `1px solid ${theme.border}`,
                display: 'grid',
                gap: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '7px 12px',
                  borderRadius: 999,
                  background: theme.accentSoftBg,
                  color: theme.accent,
                  fontSize: '12px',
                  fontWeight: 800,
                }}>
                  FET Reading
                </span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '7px 12px',
                  borderRadius: 999,
                  background: theme.surfaceSoft,
                  color: theme.body,
                  fontSize: '12px',
                  fontWeight: 800,
                }}>
                  45 min
                </span>
              </div>

              <div>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: theme.text, lineHeight: 1.3 }}>
                  {availableExam.title || 'FET Reading Exam'}
                </h3>
                <p style={{ margin: '10px 0 0', fontSize: '14px', color: theme.body, lineHeight: 1.75 }}>
                  {availableExam.description || 'Open this exam to begin the FET reading flow with practice mode, mistake review and a 45-min exam mode.'}
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate(`/?mode=reading-onboarding&examId=${availableExam.id}`)}
                style={{
                  height: '48px',
                  borderRadius: '14px',
                  border: 'none',
                  background: theme.ctaBg,
                  color: theme.ctaText,
                  fontWeight: 800,
                  fontSize: '15px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Open This Exam
              </button>
            </div>
          )) : (
            <div style={{
              background: theme.surfaceSoft,
              borderRadius: '20px',
              padding: '24px',
              border: `1px solid ${theme.border}`,
              color: theme.body,
              lineHeight: 1.8,
            }}>
              No FET reading exams are available right now. An admin needs to publish at least one exam with reading content from the admin panel.
            </div>
          )}
        </div>

        {/* Showing N of M */}
        {!loading && readingReadyExams.length > 0 && (
          <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '13px', color: theme.muted }}>
            Showing {readingReadyExams.length} of {readingReadyExams.length} exam{readingReadyExams.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}
